import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useAuth } from '../../../../context/AuthContext';
import { useConfirmDialog } from '../../../../hooks/mui/useConfirmDialog';
import {
  clearScannerSession,
  getStoredScanner,
  resumeScannerSession,
  scannerStaffLogin,
} from '../../../../services/scannerSession';
import { ScannerView } from '../ScannerPage';
import { DeviceScannerHeader } from '../ScannerPage/ScannerHeader';
import { useIdleTimer } from '../ScannerPage/hooks/useIdleTimer';
import StaffPicker from './StaffPicker';

// Staff are logged out after this long without touching the phone
export const STAFF_IDLE_LOGOUT_MS = 5 * 60 * 1000;

/**
 * /scanner — a phone logged in as a scanner. Without staff login it books
 * straight away as the scanner; with staff login someone must pick their
 * name and enter their PIN first, and every booking is theirs.
 */
export default function ScannerDevicePage() {
  const { t } = useTranslation(['inventory', 'common']);
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading, adoptLogin, logout, hasScope } = useAuth();
  const { showConfirmDialog, ConfirmDialog } = useConfirmDialog();
  const [session, setSession] = useState(null);
  const [status, setStatus] = useState('loading');

  const stored = getStoredScanner();
  const staffLogin = session?.scanner.require_staff_login ?? stored?.scanner?.require_staff_login;

  const resume = useCallback(async () => {
    try {
      setSession(await resumeScannerSession());
      setStatus('ready');
    } catch (err) {
      if (err.expired) navigate('/login', { replace: true });
      else setStatus('offline');
    }
  }, [navigate]);

  useEffect(() => {
    if (!getStoredScanner()) {
      navigate('/login', { replace: true });
      return;
    }
    resume();
  }, [navigate, resume]);

  // A scanner without staff login books as itself, never as a leftover staff session
  useEffect(() => {
    if (session && !session.scanner.require_staff_login && isAuthenticated) logout();
  }, [session, isAuthenticated, logout]);

  const logoutStaff = useCallback(async () => {
    await logout();
    resume(); // fresh staff list for the next person
  }, [logout, resume]);

  useIdleTimer(STAFF_IDLE_LOGOUT_MS, logoutStaff, { enabled: Boolean(staffLogin && isAuthenticated) });

  const logoutDevice = () => showConfirmDialog({
    title: t('inventory:scanner.device.logout_device_title'),
    message: t('inventory:scanner.device.logout_device_message'),
    severity: 'warning',
    confirmText: t('inventory:scanner.device.logout_device'),
    onConfirm: async () => {
      if (isAuthenticated) await logout();
      clearScannerSession();
      navigate('/login', { replace: true, state: { redirect_infoscreen: false } });
    },
  });

  if (status === 'loading' || isLoading) {
    return (
      <Box sx={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (status === 'offline') {
    return (
      <Box sx={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, p: 3 }}>
        <Typography textAlign="center">{t('inventory:scanner.device.offline')}</Typography>
        <Button variant="contained" size="large" onClick={() => { setStatus('loading'); resume(); }}>
          {t('inventory:scanner.device.retry')}
        </Button>
      </Box>
    );
  }

  const scannerName = session.scanner.name;
  const defaultLocationId = session.scanner.default_location_id;

  if (staffLogin && !isAuthenticated) {
    return (
      <Box sx={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
        <DeviceScannerHeader who={scannerName} onLogout={logoutDevice} logoutLabel={t('inventory:scanner.device.logout_device')} />
        <StaffPicker
          staff={session.staff}
          onPinLogin={(member, pin) => adoptLogin(() => scannerStaffLogin({ staff_member_name: member.name, pin }))}
          onPasswordLogin={(member, password) => adoptLogin(() => scannerStaffLogin({ staff_member_name: member.name, password }))}
        />
        <ConfirmDialog />
      </Box>
    );
  }

  return (
    <>
      <ScannerView
        header={staffLogin ? (
          <DeviceScannerHeader who={user?.name} user={user} onLogout={logoutStaff} logoutLabel={t('inventory:scanner.device.logout_staff')} />
        ) : (
          <DeviceScannerHeader who={scannerName} onLogout={logoutDevice} logoutLabel={t('inventory:scanner.device.logout_device')} />
        )}
        defaultLocationId={defaultLocationId}
        locationStorageKey={`orbit_scanner_location_${session.scanner.scanner_id}`}
        staffSignedIn={Boolean(staffLogin)}
        canCreateItem={staffLogin && hasScope('inventory:item:create')}
        canCreateLot={staffLogin && hasScope('inventory:lot:create')}
        canOpenFiles={Boolean(staffLogin)}
      />
      <ConfirmDialog />
    </>
  );
}
